/**
 * This is just a dumb selector component that lets the user move items form one list to another
 * The component is using internal state to manage its source/destination lists.
 * Updating the original list happens via props.handleSelectionChange only.
 */
import * as _ from "lodash";
import * as React from "react";
import { CSSProperties } from "react";

import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import {Button} from "react-bootstrap";


export interface SelectionItem {
    id: string
    content: string
    isSelected: boolean
}
interface ListSelectorProps {
    items: SelectionItem[]
    handleSelectionChange: (items: SelectionItem[], selected: SelectionItem[]) => void
    // reverse will switch the position between the source and destination items.
    reverse?: boolean
    dividerButton?: boolean
    studentsFormat?: boolean
}

interface ListSelectorState {
    items: SelectionItem[]
    selected: SelectionItem[]
    focusedItem: SelectionItem | null
}

/** The two ListSelectorState fields that hold a draggable list. */
type ListKey = "items" | "selected";

export class ListSelector extends React.Component<ListSelectorProps, ListSelectorState> {

    /**
     * selection item reordering
     */
    reorder = (list: SelectionItem[], startIndex: number, endIndex: number) => {
        const result = [...list];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    /**
     * Moves an item from one list to another list.
     */
    move = (source: SelectionItem[], destination: SelectionItem[], droppableSource: any, droppableDestination: any) => {
        const sourceClone = [...source];
        const destClone = [...destination];
        const [removed] = sourceClone.splice(droppableSource.index, 1);

        destClone.splice(droppableDestination.index, 0, removed);

        const result: any = {};
        result[droppableSource.droppableId] = sourceClone;
        result[droppableDestination.droppableId] = destClone;

        return result;
    };

    static getListItems = (isSelected: boolean, props: ListSelectorProps, items?: SelectionItem[]): SelectionItem[] => {
        return _.filter(items ? items : props.items, { isSelected: isSelected });
    };

    static getDerivedStateFromProps(props, state) {
        const difference = _.differenceWith(props.items, state.items.concat(state.selected), _.isEqual);
        if (difference.length > 0) {
            return {
                items: ListSelector.getListItems(false, props),
                selected: ListSelector.getListItems(true, props)
            };
        }

        return null;
    }

    state: ListSelectorState = {
        items: ListSelector.getListItems(false, this.props),
        selected: ListSelector.getListItems(true, this.props),
        focusedItem: null
    };

    /** Maps a droppable's id to the state list it renders. */
    id2List: { [idx: string]: ListKey } = {
        droppableSource: "items",
        droppableDestination: "selected"
    };

    getList = (id: string) => this.state[this.id2List[id]];

    getItemStyle = (isDragging: boolean, draggableStyle: CSSProperties | null) => ({
        // change background colour if dragging
        cursor: isDragging ? "grabbing" : "grab",
        ...draggableStyle
    });

    getListStyle = (isDraggingOver: boolean) => ({
        borderColor: isDraggingOver ? "#c96bc6" : "#C0C0C0"
    });

    onDragEnd = (result: DropResult) => {
        const { source, destination } = result;

        // dropped outside the list
        if (!destination) return;

        // drag n drop within the same list
        if (source.droppableId === destination.droppableId) {

            const items = this.reorder(this.getList(source.droppableId), source.index, destination.index);

            const listKey = this.id2List[destination.droppableId];

            this.setState({ [listKey]: items } as Pick<ListSelectorState, ListKey>, () => {
                this.props.handleSelectionChange(this.state.items, this.state.selected);
            });

        } else {
            const result = this.move(this.getList(source.droppableId), this.getList(destination.droppableId), source, destination);

            this.setState({
                items: result.droppableSource,
                selected: result.droppableDestination
            }, () => {
                this.props.handleSelectionChange(this.state.items, this.state.selected);
            });
        }
    };

    onFocusDraggable = (item: SelectionItem) => {
        this.setState({focusedItem: item});
    };

    onBlurDraggable = () => {
        setTimeout(() => this.setState({focusedItem: null}), 500);
    };

    onClickArrow = () => {
        const focusedItem = this.state.focusedItem;

        const found = this.state.selected.find(item => item.id === focusedItem?.id);
        if (found) {
            const selected = this.state.selected.filter(item => item.id !== focusedItem?.id);
            const items = focusedItem ? [...this.state.items, focusedItem] : [...this.state.items];
            this.setState({selected, items}, () => {
                this.props.handleSelectionChange(this.state.items, this.state.selected);
            });
        } else {
            const items = this.state.items.filter(item => item.id !== focusedItem?.id);
            const selected = focusedItem ? [...this.state.selected, focusedItem] : [...this.state.selected];
            this.setState({selected, items}, () => {
                this.props.handleSelectionChange(this.state.items, this.state.selected);
            });
        }
    };

    onClickDraggable = (event) => {
        // click was used as a part of the drag
        if(event.defaultPrevented) {
            return;
        }

        event.currentTarget.focus();
    };

    renderDroppableSource = () => {
        return (
            <Droppable droppableId="droppableSource">
                {(provided, snapshot) => (
                    <div 
                        ref={provided.innerRef}
                        style={this.getListStyle(snapshot.isDraggingOver)}
                        className="dest-source-container"
                    >
                        {this.state.items.map((item, index) => (
                            <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={index}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={this.getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style as CSSProperties | null,
                                        )}
                                        className={snapshot.isDragging && "is-dragging" || undefined}
                                        onFocus={() => this.onFocusDraggable(item)}
                                        onBlur={this.onBlurDraggable}
                                        onClick={this.onClickDraggable}>
                                        {item.content}
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    };

    renderDroppableDestination = () => {
        return (
            <Droppable droppableId="droppableDestination">
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        style={this.getListStyle(snapshot.isDraggingOver)}
                        className="dest-source-container"
                    >
                        {this.state.selected.map((item, index) => (
                            <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={index}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={this.getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style as CSSProperties | null,
                                        )}
                                        className={snapshot.isDragging && "is-dragging" || undefined}
                                        onBlur={this.onBlurDraggable}
                                        onFocus={() => this.onFocusDraggable(item)}
                                        onClick={this.onClickDraggable}>
                                        {item.content}
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    };

    render() {
        return (
            <div className="selector-lists-container">
                <DragDropContext onDragEnd={this.onDragEnd}>
                    {this.props.studentsFormat ? 
                        <div className="students-format">
                            <div className="title-draggable-section">Current Groups</div>
                            <div className="mb-3 draggable-section">
                                {this.props.reverse ? this.renderDroppableDestination(): this.renderDroppableSource()}
                            </div>
                        </div> :
                        this.props.reverse ? this.renderDroppableDestination(): this.renderDroppableSource()
                    }
                    {this.props.dividerButton ? (
                        <div className={"divider-button"}>
                            <Button variant="icon" onClick={this.onClickArrow}></Button>
                        </div>
                    ) : <div className="divider"></div>}
                    {this.props.studentsFormat ? 
                        <div className="students-format">
                            <div className="title-draggable-section">Available Groups</div>
                            <div className="mb-3 draggable-section">
                                {this.props.reverse ? this.renderDroppableSource() : this.renderDroppableDestination()}
                            </div>
                        </div> :
                        this.props.reverse ? this.renderDroppableSource() : this.renderDroppableDestination()
                    }
                </DragDropContext>
            </div>
        );
    }
}